import type { MigrationDevice } from '../api/migrations';
import type { StateName } from '../theme/tokens';
import { PHASE_LABELS, phaseIndexForActionId } from './devicePhase';

// The operational view's arithmetic (FD-498, mockup 9).
//
// THREE DISTRIBUTIONS AND A CURVE, all derived from the devices the campaign
// detail response already carried. Nothing here fetches, and nothing here is
// stored: the same posture `campaignLifecycle.ts` and `readinessRollup.ts` take,
// and for the same reason — two readers of one rule will eventually disagree,
// and a rule inside a component cannot be asserted against a fixture.
//
// NOTHING HERE READS `Migrations.Status`. It is written `"active"` at creation
// and no writer in the codebase ever updates it (FD-269). The device lifecycle
// that actually moves is `DeviceMigrations.Status` + `TerminalOutcome`, and the
// step within it is `LastActionId` (FD-496/FD-497).
//
// EVERY DISTRIBUTION ADDS UP, BY CONSTRUCTION. `total` is the SUM of the
// buckets rather than a separately-sourced figure, and an unrecognised value
// lands in an `other` bucket rather than being dropped — the `GroupStatusBar`
// rule ("rendering only the known keys would make the bar lie about its own
// total") and the FD-472 tab-count rule, applied here. A total a reader can
// falsify is worse than no total.

/**
 * The FD-190 device lifecycle, plus `other` for anything this console has not
 * been taught.
 *
 * `aborted` is ITS OWN STATE and is never folded into `failed`. FD-ADR-035 H2
 * excludes it from the retry set server-side by design — those devices were
 * deliberately stopped — and FA-ADR-021 makes `salvaged` a designed recovery
 * outcome rather than a crash. Collapsing the three is the FD-246 mistake this
 * codebase keeps refusing: red means broken, amber means look at this.
 */
export type DeviceLifecycle =
  | 'notStarted'
  | 'inProgress'
  | 'completed'
  | 'aborted'
  | 'salvaged'
  | 'faulted'
  | 'other';

export interface CountBucket {
  key: string;
  label: string;
  count: number;
  tone: StateName;
  /** One line, in the operator's terms. Used as the tile caption / bar title. */
  description: string;
  /** Rendered even at zero. A bucket that is only news when non-zero is not. */
  always: boolean;
}

export interface Distribution {
  /** The sum of every bucket — never a separately-sourced number. */
  total: number;
  /** Every bucket, zeros included, in reading order. */
  buckets: CountBucket[];
  /** `always` buckets plus any non-empty one — what a surface should render. */
  visible: CountBucket[];
}

/** Lowercase, and separators stripped, so `InProgress` and `in-progress` agree. */
function normalise(value: string | null | undefined): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

const TERMINAL: Record<string, DeviceLifecycle> = {
  completed: 'completed',
  complete: 'completed',
  aborted: 'aborted',
  salvaged: 'salvaged',
  faulted: 'faulted',
};

const RUNNING = new Set(['inprogress', 'running']);
const NOT_STARTED = new Set(['pending', 'initialized', 'initialised']);

/**
 * READS BOTH `status` AND `terminalOutcome`, and prefers the outcome.
 *
 * FD-345 made them separate server columns precisely so a concurrent merge that
 * regresses `Status` under a surviving `TerminalOutcome` stays detectable — two
 * live rows sat at `status: in-progress` under `terminalOutcome: completed`,
 * invisible to wave evaluation, which is what FD-362's heal-on-read now keys
 * off. A tally that read only `status` would report those devices as still
 * flying on a wave that had finished; `campaignLifecycle.hasDeviceStarted` reads
 * both for the same reason, and this follows it.
 */
export function classifyDeviceLifecycle(
  device: Pick<MigrationDevice, 'status' | 'terminalOutcome'>,
): DeviceLifecycle {
  const outcome = TERMINAL[normalise(device.terminalOutcome)];
  if (outcome) return outcome;

  const status = normalise(device.status);
  const terminal = TERMINAL[status];
  if (terminal) return terminal;
  if (RUNNING.has(status)) return 'inProgress';
  if (NOT_STARTED.has(status)) return 'notStarted';
  // NOT `notStarted`. A status this console has not been taught is a different
  // thing from a device that has not begun, and saying so is the whole point of
  // keeping an `other` bucket rather than a fallback.
  return 'other';
}

// `key` is a STRING rather than a `DeviceLifecycle`: `failed` is a tile, not a
// lifecycle state — it is `salvaged + faulted` folded together on purpose, and
// only here, so the tile means exactly what the server's failed-devices read
// means. The classifier keeps the two apart.
const LIFECYCLE_META: {
  key: string;
  label: string;
  tone: StateName;
  description: string;
  always: boolean;
}[] = [
  {
    key: 'completed',
    label: 'Completed',
    tone: 'success',
    description: 'Migrated cleanly and reported a terminal outcome.',
    always: true,
  },
  {
    key: 'inProgress',
    label: 'In progress',
    tone: 'progress',
    description: 'Under way — the device has a flight plan and is working through it.',
    always: true,
  },
  {
    key: 'notStarted',
    label: 'Not started',
    tone: 'neutral',
    description: 'Assigned, but this device has not begun migrating.',
    always: true,
  },
  {
    key: 'failed',
    label: 'Failed',
    tone: 'critical',
    description: 'Salvaged or faulted — the devices to retry in a supplementary wave.',
    always: true,
  },
  {
    // Amber, and separate. `abort-wave` terminal-stamps devices that were
    // deliberately stopped; counting them as failures would put devices someone
    // chose to stop into the number an operator reads as "what went wrong".
    key: 'aborted',
    label: 'Stopped',
    tone: 'warning',
    description: 'Deliberately stopped by an abort, not a failure.',
    always: false,
  },
  {
    key: 'other',
    label: 'Unrecognised state',
    tone: 'neutral',
    description: 'A device status this console has not been taught. Counted, never dropped.',
    always: false,
  },
];

/**
 * The lifecycle tally behind the operational tiles.
 *
 * `failed` is `salvaged + faulted` — the same set the server's own
 * `GET /v1/migrations/{id}/failed-devices` returns, so the tile and the issues
 * panel below it cannot disagree about what "failed" means.
 */
export function deriveLifecycleTally(
  devices: Pick<MigrationDevice, 'status' | 'terminalOutcome'>[],
): Distribution {
  const counts: Record<string, number> = {};
  for (const device of devices) {
    const key = classifyDeviceLifecycle(device);
    const bucket = key === 'salvaged' || key === 'faulted' ? 'failed' : key;
    counts[bucket] = (counts[bucket] ?? 0) + 1;
  }
  return assemble(LIFECYCLE_META.map((meta) => ({ ...meta, count: counts[meta.key] ?? 0 })));
}

/**
 * Where are the devices in their migration, read from ONE column per device.
 *
 * The phases are `devicePhase.ts`'s own stage table — `PRE` Flight Plan, `PSH`
 * Pushback, `ENR` Enroute, `ARR` Arrival, `FIN` Complete — so the distribution
 * and the grid's Step column name a stage identically.
 *
 * TWO ABSENCES, KEPT APART (the FD-246 discipline):
 *
 *   * `notReported` — no `lastActionId`. Either the device has never reported
 *     progress (most devices, most of the time) or this Manager predates
 *     FD-497. Both are an HONEST EMPTY STATE, never a guessed phase.
 *   * `unrecognised` — an action id whose stage prefix this console has not been
 *     taught. It is NOT mapped into a phase (the `devicePhase` rule), and it is
 *     NOT dropped either, or the bar would lie about its own total.
 */
export function derivePhaseDistribution(
  devices: Pick<MigrationDevice, 'lastActionId'>[],
): Distribution {
  // Stage indices 1–5; index 0 ("Ready") is a readiness verdict rather than a
  // reported action, so no action id can ever land there.
  const counts = new Map<number, number>();
  let notReported = 0;
  let unrecognised = 0;

  for (const device of devices) {
    const raw = device.lastActionId;
    if (typeof raw !== 'string' || raw.trim() === '') {
      notReported += 1;
      continue;
    }
    const index = phaseIndexForActionId(raw);
    if (index === null) {
      unrecognised += 1;
      continue;
    }
    counts.set(index, (counts.get(index) ?? 0) + 1);
  }

  const phases: CountBucket[] = [1, 2, 3, 4, 5].map((index) => ({
    key: `phase-${index}`,
    label: PHASE_LABELS[index] ?? `Stage ${index}`,
    count: counts.get(index) ?? 0,
    tone: index === 5 ? ('success' as StateName) : ('progress' as StateName),
    description: `Devices whose last reported action was in the ${PHASE_LABELS[index]} stage.`,
    always: true,
  }));

  return assemble([
    ...phases,
    {
      key: 'notReported',
      label: 'No step reported',
      count: notReported,
      tone: 'neutral',
      description:
        'No action has been reported for these devices, so there is no step to show. That is the ordinary state before a device starts.',
      always: false,
    },
    {
      key: 'unrecognised',
      label: 'Unrecognised step',
      count: unrecognised,
      tone: 'warning',
      description:
        'The action id carries a stage prefix this console has not been taught. Counted rather than guessed at.',
      always: false,
    },
  ]);
}

const TERMINAL_META: {
  key: string;
  label: string;
  tone: StateName;
  description: string;
  always: boolean;
}[] = [
  {
    key: 'completed',
    label: 'Completed',
    tone: 'success',
    description: 'Migrated cleanly.',
    always: true,
  },
  {
    key: 'salvaged',
    label: 'Salvaged',
    tone: 'warning',
    description:
      'Made usable with a recovery admin past the point of no return. The recovery password is kept until the device migrates successfully.',
    always: true,
  },
  {
    key: 'faulted',
    label: 'Faulted',
    tone: 'critical',
    description: 'The genuine failure — the device did not reach the destination.',
    always: true,
  },
  {
    key: 'aborted',
    label: 'Stopped',
    tone: 'warning',
    description: 'Terminal-stamped by an abort rather than by anything the device did.',
    always: true,
  },
  {
    key: 'notTerminal',
    label: 'No outcome recorded',
    tone: 'neutral',
    description:
      'The wave is closed but these devices never reported a terminal outcome — worth seeing, not worth counting as a success.',
    always: false,
  },
  {
    key: 'other',
    label: 'Unrecognised outcome',
    tone: 'neutral',
    description: 'An outcome this console has not been taught. Counted, never dropped.',
    always: false,
  },
];

/**
 * The shape of a finished wave.
 *
 * This is the distribution the FD-498 amendment exists for: once a wave is
 * revoked, "how many of 500 devices completed versus salvaged versus faulted" is
 * the single most useful thing to know, and `WaveFailedDevicesPanel` answers
 * only the second half of it.
 */
export function deriveTerminalDistribution(
  devices: Pick<MigrationDevice, 'status' | 'terminalOutcome'>[],
): Distribution {
  const counts: Record<string, number> = {};
  for (const device of devices) {
    const lifecycle = classifyDeviceLifecycle(device);
    const key =
      lifecycle === 'notStarted' || lifecycle === 'inProgress'
        ? 'notTerminal'
        : lifecycle === 'other'
          ? 'other'
          : lifecycle;
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return assemble(TERMINAL_META.map((meta) => ({ ...meta, count: counts[meta.key] ?? 0 })));
}

function assemble(buckets: CountBucket[]): Distribution {
  const total = buckets.reduce((sum, bucket) => sum + bucket.count, 0);
  return {
    total,
    buckets,
    visible: buckets.filter((bucket) => bucket.always || bucket.count > 0),
  };
}

export interface CompletionPoint {
  /** The device's own `terminalUtc`. */
  iso: string;
  /** Epoch ms, so a chart can lay points out without re-parsing. */
  time: number;
  /** Devices completed at or before this point. */
  cumulative: number;
}

export interface CompletionCurve {
  points: CompletionPoint[];
  /** Devices whose outcome is `completed`, dated or not. */
  completed: number;
  /**
   * Completed devices carrying no usable `terminalUtc`.
   *
   * SURFACED RATHER THAN DROPPED. A curve that silently ends below its own
   * completed count is a chart an operator can falsify against the tile beside
   * it, and that is worse than a caption saying so.
   */
  undated: number;
}

/**
 * Cumulative completions over time — AND NOT THE MOCKUP'S STACKED TIME SERIES.
 *
 * Mockup 9 draws stacked bands for in-progress and pending as well. Those cannot
 * be drawn honestly: NOTHING STORES A TIME SERIES and there is no per-device
 * start timestamp anywhere on the wire, so every point in an "in progress at
 * 11:30" band would be invented. `terminalUtc` is a real per-device instant, so
 * a completions curve is exact and client-side.
 *
 * `lastEventTimestamp` is deliberately NOT used as a substitute start: it is
 * LAST CONTACT, not elapsed migration time, and presenting it as one is the same
 * defect in a different column (which is why this task builds no Duration).
 */
export function deriveCompletionCurve(
  devices: Pick<MigrationDevice, 'status' | 'terminalOutcome' | 'terminalUtc'>[],
): CompletionCurve {
  let completed = 0;
  const times: { iso: string; time: number }[] = [];

  for (const device of devices) {
    if (classifyDeviceLifecycle(device) !== 'completed') continue;
    completed += 1;
    const iso = device.terminalUtc;
    if (!iso) continue;
    const time = new Date(iso).getTime();
    if (Number.isNaN(time)) continue;
    times.push({ iso, time });
  }

  times.sort((a, b) => a.time - b.time);
  const points = times.map((point, index) => ({ ...point, cumulative: index + 1 }));

  return { points, completed, undated: completed - points.length };
}
